import { Component, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    NoticeType,
    NoticesService,
    PersonsOrganisationDto,
    WorkTracker,
    hasAnyPermission
} from '@flyfreely-portal-ui/flyfreely';
import { Subject, firstValueFrom, takeUntil, tap } from 'rxjs';

const DEFAULT_COLOR = '#007dd5';

@Component({
    selector: 'lotam-types-setup',
    templateUrl: './lotam-types-setup.component.html',
    styleUrls: ['./lotam-types-setup.component.scss']
})
export class LotamTypesSetupComponent {
    @Input() organisation: PersonsOrganisationDto;

    isReadOnlyAdmin = true;

    noticeTypes: NoticeType[];

    showArchived = false;
    color: string = DEFAULT_COLOR;

    createFormGroup = new FormGroup({
        name: new FormControl(undefined, Validators.required),
        description: new FormControl(undefined),
        colour: new FormControl(undefined)
    });

    private workTracker = new WorkTracker();
    working = false;
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private noticesService: NoticesService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.noticesService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshNoticeTypes());
        this.refreshPermissions();
        this.refreshNoticeTypes();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshPermissions() {
        this.isReadOnlyAdmin =
            hasAnyPermission(
                this.organisation,
                PersonsOrganisationDto.Permissions.ORGANISATION_ADMIN_VIEW
            ) &&
            !hasAnyPermission(
                this.organisation,
                PersonsOrganisationDto.Permissions.ORGANISATION_EDIT
            );

        if (this.isReadOnlyAdmin) {
            this.createFormGroup.disable();
        }
    }

    refreshNoticeTypes() {
        this.noticesService
            .findNoticeTypes({
                organisationId: this.organisation.id,
                managingOrganisationId: this.organisation.id,
                audience: 'FLIGHT_OPERATIONS'
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: noticeTypes =>
                    (this.noticeTypes = noticeTypes),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while refreshing LOTAM types: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    updateNoticeType = (value: {
        name: string;
        description: string;
        noticeId: number;
        colour: string;
    }) => {
        if (value == null || value.name == null || value.noticeId == null) {
            return Promise.reject();
        }
        const doneWorking = this.workTracker.createTracker();
        return firstValueFrom(
            this.noticesService
                .updateNoticeType({
                    id: value.noticeId,
                    name: value.name,
                    description: value.description,
                    colour: value.colour
                })
                .pipe(
                    takeUntil(this.ngUnsubscribe$),
                    tap({
                        next: () => {
                            this.logging.success('LOTAM type updated');
                            doneWorking();
                        },
                        error: (error: FlyFreelyError) => {
                            this.logging.error(
                                error,
                                'Could not update LOTAM type'
                            );
                            doneWorking();
                        }
                    })
                )
        );
    };

    addNoticeType() {
        const value = this.createFormGroup.value;
        this.noticesService
            .createNoticeType({
                name: value.name,
                description: value.description,
                colour: value.colour,
                organisationId: this.organisation.id,
                audience: 'FLIGHT_OPERATIONS'
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    this.logging.success(`Successfully added LOTAM type`);
                    this.refreshNoticeTypes();
                    this.createFormGroup.setValue({
                        name: null,
                        description: null,
                        colour: null
                    });
                    this.createFormGroup.markAsPristine();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error adding LOTAM type: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    archiveNoticeType(notice: NoticeType) {
        this.noticesService
            .archiveNoticeType(notice.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: results => (notice.archived = results.archived),
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while archiving LOTAM type: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    unArchiveNoticeType(notice: NoticeType) {
        this.noticesService
            .unarchiveNoticeType(notice.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: results => (notice.archived = results.archived),
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while unarchiving LOTAM type: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    clearColourSelection() {
        this.createFormGroup.patchValue({
            colour: null
        });
    }
}
